import { useEffect, useState } from "react";

// Assets
import eventImagePlaceholder from "assets/image/user-event-placeholder.jpg";
import badgeFireside from "assets/svg/badge-fireside.png";
import badgeMotivation from "assets/svg/badge-motivation.svg";
import badgeMotivation2 from "assets/svg/badge-motivation2.svg";
import badgeMotivation3 from "assets/svg/badge-motivation3.svg";
import badgeKeynote from "assets/svg/badge-keynote.svg";
import badgePanelist from "assets/svg/badge-panelist.svg";

// Libraries
import moment from "moment";
import { motion, LayoutGroup } from "framer-motion";

// Services and Lib
import instance from "lib/axiosInterceptor";
import axios from "axios";
import { baseUrl } from "utils/baseUrl";

// Types and Interfaces
import {
  EventsResponseInterface,
  EventsInterface,
} from "interfaces/interfaces-data";
import { FetchedBookings } from "interfaces/Bookings";
import {
  BadgeData,
  EventCardInterface,
  GetTestimonialResponse,
  SpeakerLandingPageProps,
} from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import Header from "./Header";
import AboutSection from "./AboutSection";
import BadgesSection from "./BadgesSection";
import ExpertiseSection from "./ExpertiseSection";
import PastEvents from "./PastEvents";
import TestimonialsSection from "./TestimonialsSection";
import Footer from "./Footer";
import VideoHighlight from "./VideoHighlight";
import NewHeroSection from "./NewHeroSection";
// import { EventReviewsSummary } from "interfaces";
import Loader from "components/UI/Loader";
import useDocumentTitle from "hooks/useDocumentTitle";
import useSpeakerData from "./useSpeakerData";
import { getUserDetails } from "utils/localStorageServices";

// Expertise Component

// Main App Component
function SpeakerLandingPage({ username }: SpeakerLandingPageProps) {
  const {
    speakerData,
    setSpeakerData,
    expertise,
    videoHighlight,
    isSpeakerLoading,
  } = useSpeakerData(username);

  useDocumentTitle(`${speakerData.name} - StagePanda`);

  // const [upcomingEventData, setUpcomingEventData] =
  //   useState<UpcomingEventData | null>(null);
  const [pastEventsData, setPastEventsData] = useState<EventCardInterface[]>(
    [],
  );
  const [testimonials, setTestimonials] = useState<GetTestimonialResponse[]>(
    [],
  );
  const [isBookingsLoading, setIsBookingsLoading] = useState(true);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [leastBooking, setLeastBooking] = useState<FetchedBookings | null>(
    null,
  );
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [shouldOpenLoginModal, setShouldOpenLoginModal] = useState(false);
  const [openInviteDrawer, setOpenInviteDrawer] = useState(false);

  // const [upComingEvents_, setUpComingEvents_] = useState<EventsInterface[]>([]);

  const userDetails = getUserDetails();

  useEffect(() => {
    const isUserLoggedIn = !!userDetails;
    setIsUserLoggedIn(isUserLoggedIn);
  }, [userDetails]);

  const isPageOwner =
    isUserLoggedIn &&
    !!userDetails?.email &&
    !!speakerData.email &&
    userDetails.email.toLowerCase() === speakerData.email.toLowerCase();

  // const isLoading = isSpeakerLoading || isEventsLoading || isBookingsLoading;
  const isLoading = isSpeakerLoading;

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get<GetTestimonialResponse[]>(
          `${baseUrl}/Speaker/Testimonials/${username}`,
        );
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };

    fetchTestimonials();
  }, [username]);

  // get bookings to get the minimum price
  useEffect(() => {
    async function fetchBookings(userName: string) {
      try {
        setIsBookingsLoading(true);
        const response = await instance.get(
          `/Bookings/Calendar?username=${userName}`,
        );
        if (response?.data) {
          const filteredBookings: FetchedBookings[] = response.data.filter(
            (fetchedBookings_: FetchedBookings) => !fetchedBookings_.deleted,
          );

          const sortedBookings = filteredBookings.sort(
            (a, b) => a.price - b.price,
          );

          if (sortedBookings.length > 0) {
            const minPrice = sortedBookings[0].price;
            setSpeakerData((prev) => ({
              ...prev,
              minPrice: minPrice,
            }));

            setLeastBooking(sortedBookings[0]);
          }
        }
      } catch (e) {
      } finally {
        setIsBookingsLoading(false);
      }
    }
    username && fetchBookings(username);
  }, [username, setSpeakerData]);

  // Fetch and filter events
  useEffect(() => {
    const getEvents = async () => {
      setIsEventsLoading(true);
      try {
        // Fetch events without teamId since API doesn't require it
        const response = await axios.get<EventsResponseInterface>(
          `${baseUrl}/Events?speakerId=${username}&StartDate=2023-05-22&pageLimit=100&extendSpeakerSummary=true`,
        );

        // Extract events from the nested structure
        let allEvents: EventsInterface[] = response.data.data.events;

        // Fetch reviews for each event and append to allEvents
        // const eventsWithReviews: (EventsInterface & {
        //   reviewsSummary: EventReviewsSummary;
        // })[] = await Promise.all(
        //   allEvents.map(async (event) => {
        //     const params = {
        //       eventId: event.id,
        //     };
        //     try {
        //       const reviewsResponse = await axios.get(
        //         `${baseUrl}/Events/Reviews`,
        //         { params },
        //       );
        //       return {
        //         ...event,
        //         reviews: reviewsResponse.data.data,
        //         reviewsSummary: reviewsResponse.data.summary,
        //       };
        //     } catch (error) {
        //       console.error(
        //         `Error fetching reviews for event ${event.id}:`,
        //         error,
        //       );
        //       // Return event without reviews if fetch fails
        //       return {
        //         ...event,
        //         reviews: { data: [], totalCount: 0, returned: 0, skipped: 0 },
        //         reviewsSummary: null,
        //       };
        //     }
        //   }),
        // );

        // allEvents = eventsWithReviews as any;

        const now = moment();

        // Filter past events (ended events)
        const pastEvents = allEvents
          .filter((event) => {
            const endDate = moment(event.endDate || event.startDate);
            return endDate.isBefore(now) && event.status === 2; // status 2 = Ended
          })
          .sort((a, b) => {
            // Sort by most recent (descending)
            return moment(b.endDate || b.startDate).diff(
              moment(a.endDate || a.startDate),
            );
          })
          .slice(0, 3) // Get the 3 most recent past events
          .map((event) => ({
            title: event.title,
            image: event.banner || eventImagePlaceholder,
            rating: "4.5 / 5.0", // Default rating as API doesn't provide this
            attendees: "550+ participants", // Default attendees as API doesn't provide this
          }));

        // Filter upcoming events (scheduled or started, but not ended)
        const upcomingEvents = allEvents
          .filter((event) => {
            const startDate = moment(event.startDate);
            return (
              startDate.isAfter(now) &&
              (event.status === 0 || event.status === 1)
            ); // status 0 = Scheduled, 1 = Started
          })
          .sort((a, b) => {
            // Sort by nearest upcoming date (ascending)
            return moment(a.startDate).diff(moment(b.startDate));
          });

        // setUpComingEvents_(upcomingEvents);

        setPastEventsData(
          allEvents.map((event) => ({
            id: event.id,
            title: event.title,
            image: event.banner || eventImagePlaceholder,
            date: event.timeStarted || event.startDate,
            attendees: event.attendees || 0,
            engagementScore: event.engagementScore || 0,
            rating: event.rating || 0,
            ratingCount: event.ratingCount || 0,
          })),
        );

        // Set past events data
        // if (pastEvents.length > 0) {
        //   setPastEventsData(pastEvents);
        // } else {
        // }

        // Set upcoming event data (only the nearest one)
        // if (upcomingEvents.length > 0) {
        //   const nextEvent = upcomingEvents[0];
        //   const eventStartDate = moment(nextEvent.startDate);
        //   const duration = moment.duration(eventStartDate.diff(now));

        //   const upcomingData = {
        //     title: nextEvent.title,
        //     description: nextEvent.description || "No description provided",
        //     countdown: {
        //       days: Math.max(0, Math.floor(duration.asDays())),
        //       hours: Math.max(0, duration.hours()),
        //       mins: Math.max(0, duration.minutes()),
        //       secs: Math.max(0, duration.seconds()),
        //     },
        //   };

        //   setUpcomingEventData(upcomingData);
        // } else {
        // }
      } catch (e) {
        console.error("Error fetching events:", e);
      } finally {
        setIsEventsLoading(false);
      }
    };

    getEvents();
  }, [username]); // Remove teamInfo dependency since we're not using it

  // Map the badge names to their corresponding images
  const badgeMap: Record<string, string> = {
    keynote: badgeKeynote,
    panelist: badgePanelist,
    fireside: badgeFireside,
    motivation: badgeMotivation,
    motivation2: badgeMotivation2,
    motivation3: badgeMotivation3,
    "competition/pitch": badgePanelist, // Map Competition/Pitch to panelist badge
    workshop: badgeFireside, // Map Workshop to fireside badge
    competition: badgePanelist,
    pitch: badgePanelist,
  };

  // Transform speaker badges into BadgeData format
  const badges: BadgeData[] = speakerData.badges.map((badge) => {
    const badgeLower = badge.badge.toLowerCase();
    // Find matching badge image
    const badgeImage = badgeMap[badgeLower] || badgeMap.keynote; // Default to keynote if not found

    return {
      color: "", // Not needed anymore but kept for interface compatibility
      label: `${badge.badge}`,
      badge: badgeImage,
      count: badge.count || 1,
    };
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-orange1000 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  function handleOpenLoginModal() {
    setShouldOpenLoginModal(true);
  }

  return (
    <div className="min-h-screen bg-orange1000 flex justify-center px-4 font-monaRegular">
      <div className="w-full max-w-[1200px]">
        <Header
          callbackForLogout={() => {
            setIsUserLoggedIn(false);
          }}
          openInviteDrawer={openInviteDrawer}
          setOpenInviteDrawer={setOpenInviteDrawer}
          shouldOpenLoginModal={shouldOpenLoginModal}
          setShouldOpenLoginModal={setShouldOpenLoginModal}
        />
        <NewHeroSection
          speaker={speakerData}
          booking={leastBooking}
          userName={username}
          isUserLoggedIn={isUserLoggedIn}
          isPageOwner={isPageOwner}
          handleOpenLoginModal={handleOpenLoginModal}
          openInviteDrawer={openInviteDrawer}
          setOpenInviteDrawer={setOpenInviteDrawer}
        />

        <div className="w-full mt-4 sm:mt-[56.33px]">
          <div className="flex gap-5 flex-col lg:flex-row">
            <div className="w-full lg:w-[67%]">
              <AboutSection bio={speakerData.description} />
            </div>
            <LayoutGroup>
              <div className="w-full lg:w-[33%] mt-4 sm:mt-0">
                <motion.div
                  layout
                  transition={{
                    layout: { duration: 0.6, ease: [0.4, 0.0, 0.2, 1] },
                  }}
                >
                  <ExpertiseSection expertise={expertise} />
                </motion.div>
                <motion.div
                  layout
                  transition={{
                    layout: { duration: 0.6, ease: [0.4, 0.0, 0.2, 1] },
                  }}
                  className="mt-4 sm:mt-[37px]"
                >
                  <BadgesSection
                    badges={badges}
                    userName={username}
                    badgeMap={badgeMap}
                    speakerData={speakerData}
                  />
                </motion.div>
              </div>
            </LayoutGroup>
          </div>
        </div>

        {videoHighlight.videoUrl && (
          <VideoHighlight
            videoUrl={videoHighlight.videoUrl}
            thumbnail={videoHighlight.thumbnail}
          />
        )}

        {pastEventsData.length > 0 && <PastEvents events={pastEventsData} />}

        {testimonials.length > 0 && (
          <TestimonialsSection testimonials={testimonials} />
        )}
        <div className="mt-[188.78px] mb-[107px]">
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default SpeakerLandingPage;
